<script lang="ts" setup>
import { checkEnvSite } from "~/server/utils/getAlgoliaConfig"
import { mapProductsAlgolia, ProductAlgolia } from "~/utils/mapProducts"
import { buildExcludeFilter, EAnalyticsTag } from "~/utils/algolia"
import type {
  IndexWidget,
  InstantSearchOptions,
  Widget
} from "instantsearch.js"

const indexName: string = `TOP_PRODUCTS${checkEnvSite}`
const router = useRouter()

const formatProducts = (items: ProductAlgolia[]) => {
  return mapProductsAlgolia(items)
}

const sortingItems = ref([
  {
    value: `TOP_PRODUCTS${checkEnvSite}`,
    label: "Miglior risultato"
  },
  {
    value: `TOP_PRODUCTS${checkEnvSite}_discount-desc`,
    label: "Sconto decrescente"
  },
  {
    value: `TOP_PRODUCTS${checkEnvSite}_name-asc`,
    label: "Nome prodotto A-Z"
  },
  {
    value: `TOP_PRODUCTS${checkEnvSite}_name-desc`,
    label: "Nome prodotto Z-A"
  },
  {
    value: `TOP_PRODUCTS${checkEnvSite}_price-asc`,
    label: "Prezzo crescente"
  },
  {
    value: `TOP_PRODUCTS${checkEnvSite}_price-desc`,
    label: "Prezzo decrescente"
  }
])

const configuration = ref<null | InstantSearchOptions>(null)
const widgets = ref<null | Array<Widget | IndexWidget>>(null)
const client = useAlgoliaRef()
const stateMapping = useStateMapping()
const algoliaRouter = useCustomRouting()

configuration.value = {
  indexName: indexName,
  insights: true,
  routing: {
    router: algoliaRouter.value.router,
    stateMapping: stateMapping(indexName)
  },
  searchClient: client
}

watch(
  router.currentRoute,
  () => {
    configuration.value = {
      indexName: indexName,
      insights: true,
      routing: {
        router: algoliaRouter.value.router,
        stateMapping: stateMapping(indexName)
      },
      searchClient: client
    }

    widgets.value = [
      useAisHits({
        // @ts-ignore
        transformItems: (items) => formatProducts(items)
      }),
      useAisSearchBox({}),
      useAisConfigure({
        searchParameters: {
          query: algoliaRouter.value.router.read().query,
          hitsPerPage: 40,
          analyticsTags: [EAnalyticsTag.srp],
          page: router.currentRoute.value.params?.page
            ? router.currentRoute.value.params.page - 1
            : 0,
          distinct: true,
          clickAnalytics: true,

          filters: `price.amount_cents > 1`,
          ...buildExcludeFilter({ excludeOutOfMarket: true })
        }
      }),
      useAisStats({}),
      useAisSortBy({
        items: sortingItems.value
      }),
      useAisClearRefinements({}, "all"),
      useAisClearRefinements(
        {
          includedAttributes: ["price.amount_float"]
        },
        "price.amount_float"
      ),
      useAisClearRefinements(
        {
          includedAttributes: ["brand.name"]
        },
        "brand.name"
      ),
      useAisClearRefinements(
        {
          includedAttributes: ["mainCategory.name"]
        },
        "mainCategory.name"
      ),
      useAisClearRefinements(
        {
          includedAttributes: ["inStock", "deductibleProduct"]
        },
        "availability"
      ),
      useAisRefinementList({
        attribute: "brand.name"
      }),
      useAisCurrentRefinements({}, "all"),
      useAisCurrentRefinements(
        {
          includedAttributes: ["brand.name"]
        },
        "brand.name"
      ),
      useAisCurrentRefinements(
        {
          includedAttributes: ["mainCategory.name"]
        },
        "mainCategory.name"
      ),
      useAisRangeInput({
        attribute: "price.amount_float"
      }),
      useAisRefinementList({
        attribute: "mainCategory.name"
      }),
      useAisToggleRefinement({
        attribute: "isDiscounted"
      }),
      useAisToggleRefinement({
        attribute: "inStock"
      }),
      useAisToggleRefinement({
        attribute: "deductibleProduct"
      })
    ] as any
  },
  { immediate: true }
)
</script>

<template>
  <div>
    <AisInstantSearch
      :widgets="widgets"
      :configuration="configuration"
      v-if="widgets && configuration"
    >
      <slot />
    </AisInstantSearch>
    <div v-else>
      <PagesNoResults :product="''" />
    </div>
  </div>
</template>
